"use client";

import { motion } from "framer-motion";
import { useTranslation } from "../hooks/useTranslation";
import { useIsMobile } from "../hooks/useIsMobile";
import GradientButton from "./GradientButton";
import Image from "next/image";
import { useEffect, useState } from "react";

interface WelcomeSectionProps {
	pricingId?: string;
	aboutId?: string;
	lessonsId?: string;
	testimonialsId?: string;
	contactId?: string;
}

export default function WelcomeSection({
	pricingId,
	aboutId,
	lessonsId,
	testimonialsId,
	contactId,
}: WelcomeSectionProps) {
	const { t, loading } = useTranslation();
	const [isHydrated, setIsHydrated] = useState(false);
	const isMobile = useIsMobile();

	// Prevent animations until after hydration to avoid blinking
	useEffect(() => {
		setIsHydrated(true);
	}, []);

	// Don't render until translations are loaded and hydration is complete
	if (loading || !isHydrated) {
		return (
			<section className="min-h-[60vh] flex flex-col justify-start px-4 sm:px-6 lg:px-8 relative overflow-hidden pt-24 md:pt-32 pb-4 md:pb-6">
				<div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-gray-50"></div>
				<div className="text-center">
					<div
						className={`rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto ${isMobile ? "" : "animate-spin"}`}
					></div>
					<p className="mt-4 text-gray-600">Loading...</p>
				</div>
			</section>
		);
	}

	const scrollToPricing = () => {
		if (pricingId) {
			const pricingSection = document.getElementById(pricingId);
			if (pricingSection) {
				pricingSection.scrollIntoView({ behavior: "smooth" });
			}
		} else {
			// Fallback to querySelector if no pricingId provided
			const pricingSection = document.querySelector('[id*="pricing"]');
			if (pricingSection) {
				pricingSection.scrollIntoView({ behavior: "smooth" });
			}
		}
	};

	const scrollToAbout = () => {
		if (aboutId) {
			const aboutSection = document.getElementById(aboutId);
			if (aboutSection) {
				aboutSection.scrollIntoView({ behavior: "smooth" });
			}
		}
	};

	const scrollToLessons = () => {
		if (lessonsId) {
			const lessonsSection = document.getElementById(lessonsId);
			if (lessonsSection) {
				lessonsSection.scrollIntoView({ behavior: "smooth" });
			}
		}
	};

	const scrollToTestimonials = () => {
		if (testimonialsId) {
			const testimonialsSection = document.getElementById(testimonialsId);
			if (testimonialsSection) {
				testimonialsSection.scrollIntoView({ behavior: "smooth" });
			}
		}
	};

	const scrollToContact = () => {
		if (contactId) {
			const contactSection = document.getElementById(contactId);
			if (contactSection) {
				contactSection.scrollIntoView({ behavior: "smooth" });
			}
		}
	};

	return (
		<section className="min-h-[60vh] flex flex-col justify-start px-4 sm:px-6 lg:px-8 relative overflow-hidden pt-12 md:pt-32 pb-4 md:pb-6">
			{/* Clean professional background */}
			<div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-gray-50"></div>

			<div className="max-w-6xl mx-auto relative z-10">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					{/* Trust Indicators - Mobile Only */}
					{/* <motion.div
						initial={isMobile ? false : { opacity: 0, y: 30 }}
						animate={isMobile ? false : { opacity: 1, y: 0 }}
						transition={isMobile ? {} : { duration: 0.8, delay: 0.6 }}
						className="flex flex-wrap justify-center items-center gap-8 text-gray-600 text-sm md:hidden"
					>
						<div className="flex items-center gap-2">
							<span className="text-green-600" aria-hidden="true">✓</span>
							<span>{t("welcome.certified")}</span>
						</div>
						<div className="flex items-center gap-2">
							<span className="text-green-600" aria-hidden="true">✓</span>
							<span>{t("welcome.fromHome")}</span>
						</div>
						<div className="flex items-center gap-2">
							<span className="text-green-600" aria-hidden="true">✓</span>
							<span>{t("welcome.flexible")}</span>
						</div>
						<div className="flex items-center gap-2">
							<span className="text-green-600" aria-hidden="true">✓</span>
							<span>Guapa, guapa y guapa</span>
						</div>
					</motion.div> */}
					{/* Profile Image - First on mobile */}
					<motion.div
						initial={isMobile ? false : { opacity: 0, x: 30 }}
						animate={isMobile ? false : { opacity: 1, x: 0 }}
						transition={isMobile ? {} : { duration: 0.8, delay: 0.2 }}
						className="flex justify-center lg:justify-end order-1 lg:order-2"
					>
						<div className="relative">
							{/* Mobile version - WebP for better performance */}
							<div className="relative w-64 h-64 sm:w-80 sm:h-80 lg:hidden">
								<div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#5afeff] via-[#fdabe5] to-[#5afeff] p-2 animate-gradient-x">
									<div className="w-full h-full rounded-full overflow-hidden bg-white">
										<Image
											src="/anastasia-profile.webp"
											alt="Anastasia - Profesora de español en Mallorca"
											width={384}
											height={384}
											className={`w-full h-full object-cover object-[center_5%] ${isMobile ? "" : "transition-transform duration-300"}`}
											priority
											fetchPriority="high"
											decoding="sync"
										/>
									</div>
								</div>
							</div>
							{/* Desktop version - JPEG for better quality */}
							<div className="relative w-96 h-96 hidden lg:block">
								<div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#5afeff] via-[#fdabe5] to-[#5afeff] p-2 animate-gradient-x">
									<div className="w-full h-full rounded-full overflow-hidden bg-white">
										<Image
											src="/anastasia-profile.jpeg"
											alt="Anastasia - Profesora de español en Mallorca"
											width={384}
											height={384}
											className={`w-full h-full object-cover object-[center_0%] ${isMobile ? "" : "hover:scale-105 transition-transform duration-300"}`}
											priority
											fetchPriority="high"
											decoding="sync"
										/>
									</div>
								</div>
							</div>
							{/* Decorative elements */}
							{/* <div className={`absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-[#5afeff] to-[#fdabe5] rounded-full ${isMobile ? '' : 'animate-pulse'}`}></div>
							<div className={`absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-r from-[#fdabe5] to-[#5afeff] rounded-full ${isMobile ? '' : 'animate-pulse delay-1000'}`}></div> */}
						</div>
					</motion.div>

					{/* Text Content - Second on mobile */}
					<motion.div
						initial={isMobile ? false : { opacity: 0, x: -30 }}
						animate={isMobile ? false : { opacity: 1, x: 0 }}
						transition={isMobile ? {} : { duration: 0.8 }}
						className="text-center lg:text-left order-2 lg:order-1"
					>
						<h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
							{t("welcome.heroTitle")}
							<span className="relative bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
								{t("welcome.heroSubtitle")}
							</span>
						</h1>

						<p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
							<span className="block mt-2 text-base text-gray-600">
								{t("welcome.heroSubdescription")}
							</span>
						</p>
					</motion.div>
				</div>

				{/* Buttons and Trust Indicators - Centered below both columns */}
				<motion.div
					initial={isMobile ? false : { opacity: 0, y: 30 }}
					animate={isMobile ? false : { opacity: 1, y: 0 }}
					transition={isMobile ? {} : { duration: 0.8, delay: 0.4 }}
					className="text-center mt-8"
				>
					{/* Mobile Navigation Buttons - Only visible on mobile */}
					<div className="block md:hidden">
						<div className="grid grid-cols-1 gap-3 mb-8">
							<button
								type="button"
								onClick={scrollToAbout}
								className={`px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-[#4ae6ff] to-[#fc9de3] border-2 border-[#4ae6ff] rounded-lg shadow-lg ${isMobile ? "" : "hover:shadow-2xl hover:brightness-110 transition-all duration-300 transform hover:scale-105"}`}
							>
								{t("nav.about")}
							</button>
							<button
								type="button"
								onClick={scrollToLessons}
								className={`px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-[#4ae6ff] to-[#fc9de3] border-2 border-[#4ae6ff] rounded-lg shadow-lg ${isMobile ? "" : "hover:shadow-2xl hover:brightness-110 transition-all duration-300 transform hover:scale-105"}`}
							>
								{t("nav.lessons")}
							</button>
							<button
								type="button"
								onClick={scrollToPricing}
								className={`px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-[#4ae6ff] to-[#fc9de3] border-2 border-[#4ae6ff] rounded-lg shadow-lg ${isMobile ? "" : "hover:shadow-2xl hover:brightness-110 transition-all duration-300 transform hover:scale-105"}`}
							>
								{t("nav.classes")}
							</button>
							<button
								type="button"
								onClick={scrollToTestimonials}
								className={`px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-[#4ae6ff] to-[#fc9de3] border-2 border-[#4ae6ff] rounded-lg shadow-lg ${isMobile ? "" : "hover:shadow-2xl hover:brightness-110 transition-all duration-300 transform hover:scale-105"}`}
							>
								{t("nav.testimonials")}
							</button>
							<button
								type="button"
								onClick={scrollToContact}
								className={`px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-[#4ae6ff] to-[#fc9de3] border-2 border-[#4ae6ff] rounded-lg shadow-lg ${isMobile ? "" : "hover:shadow-2xl hover:brightness-110 transition-all duration-300 transform hover:scale-105"}`}
							>
								{t("nav.contact")}
							</button>
						</div>

						{/* Instagram link for mobile */}
						<div className="mb-8">
							<a
								href="https://www.instagram.com/nastenka.en.mallorca/"
								target="_blank"
								rel="noopener noreferrer"
								className={`flex items-center justify-center space-x-3 px-6 py-3 text-sm font-semibold text-gray-700 bg-gradient-to-br from-[#f8b5d1] via-[#ffd1dc] to-[#ffe4e1] border-2 border-[#f8b5d1] rounded-full shadow-lg ${isMobile ? "" : "hover:from-[#f48fb1] hover:via-[#ffb3ba] hover:to-[#ffc1cc] hover:border-[#f48fb1] transition-all duration-300 hover:shadow-xl transform hover:scale-105"}`}
								aria-label="Ir al perfil de Instagram de nastenka.en.mallorca"
							>
								<span className="font-semibold">@nastenka.en.mallorca</span>
								<svg
									className="w-5 h-5 text-gray-700"
									fill="currentColor"
									viewBox="0 0 24 24"
									aria-hidden="true"
								>
									<path
										fillRule="evenodd"
										d="M12.017 0C8.396 0 7.929.01 6.71.048 5.493.087 4.73.222 4.058.42a5.916 5.916 0 0 0-2.134 1.388A5.916 5.916 0 0 0 .536 4.042C.333 4.715.198 5.478.159 6.695.122 7.913.11 8.38.11 12.017c0 3.637.011 4.104.048 5.323.039 1.217.174 1.98.372 2.652a5.916 5.916 0 0 0 1.388 2.134 5.916 5.916 0 0 0 2.134 1.388c.672.198 1.435.333 2.652.372 1.219.037 1.686.048 5.323.048 3.637 0 4.104-.011 5.323-.048 1.217-.039 1.98-.174 2.652-.372a5.916 5.916 0 0 0 2.134-1.388 5.916 5.916 0 0 0 1.388-2.134c.198-.672.333-1.435.372-2.652.037-1.219.048-1.686.048-5.323 0-3.637-.011-4.104-.048-5.323-.039-1.217-.174-1.98-.372-2.652a5.916 5.916 0 0 0-1.388-2.134A5.916 5.916 0 0 0 19.676.42c-.672-.198-1.435-.333-2.652-.372C15.805.01 15.338 0 11.701 0h.316zm-.105 2.163c3.534 0 3.95.01 5.344.048 1.29.058 1.99.27 2.458.448a4.1 4.1 0 0 1 1.526.993 4.1 4.1 0 0 1 .993 1.526c.178.468.39 1.168.448 2.458.038 1.394.048 1.81.048 5.344 0 3.534-.01 3.95-.048 5.344-.058 1.29-.27 1.99-.448 2.458a4.1 4.1 0 0 1-.993 1.526 4.1 4.1 0 0 1-1.526.993c-.468.178-1.168.39-2.458.448-1.394.038-1.81.048-5.344.048-3.534 0-3.95-.01-5.344-.048-1.29-.058-1.99-.27-2.458-.448a4.1 4.1 0 0 1-1.526-.993 4.1 4.1 0 0 1-.993-1.526c-.178-.468-.39-1.168-.448-2.458-.038-1.394-.048-1.81-.048-5.344 0-3.534.01-3.95-.048-5.344.058-1.29.27-1.99.448-2.458a4.1 4.1 0 0 1 .993-1.526 4.1 4.1 0 0 1 1.526-.993c.468-.178 1.168-.39 2.458-.448 1.394-.038 1.81-.048 5.344-.048z"
										clipRule="evenodd"
									/>
									<path
										fillRule="evenodd"
										d="M12.017 5.838a6.179 6.179 0 1 0 0 12.358 6.179 6.179 0 0 0 0-12.358zM12.017 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"
										clipRule="evenodd"
									/>
									<path d="M19.846 5.595a1.441 1.441 0 1 1-2.883 0 1.441 1.441 0 0 1 2.883 0z" />
								</svg>
							</a>
						</div>
					</div>

					{/* Desktop Buttons - Only visible on desktop */}
					<div className="hidden md:block">
						<div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
							<GradientButton
								onClick={scrollToPricing}
								className="px-10 py-5 text-xl font-semibold shadow-2xl"
							>
								{t("welcome.viewPlans")}
							</GradientButton>

							<a
								href="https://www.instagram.com/nastenka.en.mallorca/"
								target="_blank"
								rel="noopener noreferrer"
								className={`flex items-center justify-center space-x-4 px-10 py-5 text-xl font-semibold text-gray-700 bg-gradient-to-br from-[#f8b5d1] via-[#ffd1dc] to-[#ffe4e1] border-2 border-[#f8b5d1] rounded-full shadow-lg ${isMobile ? "" : "hover:from-[#f48fb1] hover:via-[#ffb3ba] hover:to-[#ffc1cc] hover:border-[#f48fb1] transition-all duration-300 hover:shadow-xl transform hover:scale-105"}`}
								aria-label="Ir al perfil de Instagram de nastenka.en.mallorca"
							>
								<span className="font-semibold">@nastenka.en.mallorca</span>
								<svg
									className="w-6 h-6 text-gray-700"
									fill="currentColor"
									viewBox="0 0 24 24"
									aria-hidden="true"
								>
									<path
										fillRule="evenodd"
										d="M12.017 0C8.396 0 7.929.01 6.71.048 5.493.087 4.73.222 4.058.42a5.916 5.916 0 0 0-2.134 1.388A5.916 5.916 0 0 0 .536 4.042C.333 4.715.198 5.478.159 6.695.122 7.913.11 8.38.11 12.017c0 3.637.011 4.104.048 5.323.039 1.217.174 1.98.372 2.652a5.916 5.916 0 0 0 1.388 2.134 5.916 5.916 0 0 0 2.134 1.388c.672.198 1.435.333 2.652.372 1.219.037 1.686.048 5.323.048 3.637 0 4.104-.011 5.323-.048 1.217-.039 1.98-.174 2.652-.372a5.916 5.916 0 0 0 2.134-1.388 5.916 5.916 0 0 0 1.388-2.134c.198-.672.333-1.435.372-2.652.037-1.219.048-1.686.048-5.323 0-3.637-.011-4.104-.048-5.323-.039-1.217-.174-1.98-.372-2.652a5.916 5.916 0 0 0-1.388-2.134A5.916 5.916 0 0 0 19.676.42c-.672-.198-1.435-.333-2.652-.372C15.805.01 15.338 0 11.701 0h.316zm-.105 2.163c3.534 0 3.95.01 5.344.048 1.29.058 1.99.27 2.458.448a4.1 4.1 0 0 1 1.526.993 4.1 4.1 0 0 1 .993 1.526c.178.468.39 1.168.448 2.458.038 1.394.048 1.81.048 5.344 0 3.534-.01 3.95-.048 5.344-.058 1.29-.27 1.99-.448 2.458a4.1 4.1 0 0 1-.993 1.526 4.1 4.1 0 0 1-1.526.993c-.468.178-1.168.39-2.458.448-1.394.038-1.81.048-5.344.048-3.534 0-3.95-.01-5.344-.048-1.29-.058-1.99-.27-2.458-.448a4.1 4.1 0 0 1-1.526-.993 4.1 4.1 0 0 1-.993-1.526c-.178-.468-.39-1.168-.448-2.458-.038-1.394-.048-1.81-.048-5.344 0-3.534.01-3.95-.048-5.344.058-1.29.27-1.99.448-2.458a4.1 4.1 0 0 1 .993-1.526 4.1 4.1 0 0 1 1.526-.993c.468-.178 1.168-.39 2.458-.448 1.394-.038 1.81-.048 5.344-.048z"
										clipRule="evenodd"
									/>
									<path
										fillRule="evenodd"
										d="M12.017 5.838a6.179 6.179 0 1 0 0 12.358 6.179 6.179 0 0 0 0-12.358zM12.017 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8z"
										clipRule="evenodd"
									/>
									<path d="M19.846 5.595a1.441 1.441 0 1 1-2.883 0 1.441 1.441 0 0 1 2.883 0z" />
								</svg>
							</a>
						</div>
					</div>

					{/* Trust Indicators - Desktop Only */}
					<motion.div
						initial={isMobile ? false : { opacity: 0, y: 30 }}
						animate={isMobile ? false : { opacity: 1, y: 0 }}
						transition={isMobile ? {} : { duration: 0.8, delay: 0.6 }}
						className="hidden md:flex flex-wrap justify-center items-center gap-8 text-gray-600 text-sm"
					>
						<div className="flex items-center gap-2">
							<span className="text-green-600" aria-hidden="true">✓</span>
							<span>{t("welcome.certified")}</span>
						</div>
						<div className="flex items-center gap-2">
							<span className="text-green-600" aria-hidden="true">✓</span>
							<span>{t("welcome.fromHome")}</span>
						</div>
						<div className="flex items-center gap-2">
							<span className="text-green-600" aria-hidden="true">✓</span>
							<span>{t("welcome.flexible")}</span>
						</div>
					</motion.div>
				</motion.div>

				{/* Stats Section */}
				{/* <motion.div
					initial={{ opacity: 0, y: 50 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.8 }}
					className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-8"
				>
					<div className="text-center p-6 bg-white/90 backdrop-blur-md rounded-2xl border border-gray-200 shadow-lg flex flex-col items-center justify-center min-h-[120px]">
						<div className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2">
							500+
						</div>
						<div className="text-gray-600 text-lg">Estudiantes satisfechos</div>
					</div>
					<div className="text-center p-6 bg-white/90 backdrop-blur-md rounded-2xl border border-gray-200 shadow-lg flex flex-col items-center justify-center min-h-[120px]">
						<div className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2">
							5+
						</div>
						<div className="text-gray-600 text-lg">Años de experiencia</div>
					</div>
					<div className="text-center p-6 bg-white/90 backdrop-blur-md rounded-2xl border border-gray-200 shadow-lg flex flex-col items-center justify-center min-h-[120px]">
						<div className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2">
							98%
						</div>
						<div className="text-gray-600 text-lg">Tasa de satisfacción</div>
					</div>
				</motion.div> */}
			</div>
		</section>
	);
}
